import { FC, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { ArcElement, Chart as ChartJS, PieController, Title, Tooltip } from "chart.js";

const plugins = [ArcElement, PieController, Title, Tooltip];
ChartJS.register(...plugins);

export interface IPieChartProps {
  datasets: any[];
  height: number;
  width: number;
}

export const PieChart: FC<IPieChartProps> = props => {
  const { datasets, height, width } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartJS<"pie"> | null>();

  const renderChart = () => {
    if (!canvasRef.current) return;

    canvasRef.current.height = height;
    canvasRef.current.width = width;
    canvasRef.current.style.height = `${height}px`;
    canvasRef.current.style.width = `${width}px`;

    // @ts-ignore
    chartRef.current = new ChartJS<"pie">(canvasRef.current, {
      type: "pie",
      data: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        datasets: datasets.map(({ label, layer, ...rest }) => ({
          ...rest,
          label: `${label as string} (${(layer as string).trim()})`,
        })),
      },
      options: {
        interaction: {
          mode: "point",
        },
        plugins: {
          ...plugins,
          title: {
            display: true,
            text: datasets[0].label,
            position: "bottom",
            color: "#000000",
            padding: {
              top: 16,
              bottom: 0,
            },
            font: { size: 20 },
          },
          // @ts-ignore
          alarmLevel: false,
          // @ts-ignore
          crosshair: false,
          // @ts-ignore
          htmlLegend: false,
        },
        responsive: false,
      },
    });
  };

  const destroyChart = () => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  };

  useEffect(() => {
    renderChart();
    return () => destroyChart();
  }, [datasets]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
      <Box sx={{ width, height }}>
        <canvas ref={canvasRef} role="img" />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
        {datasets
          .reduce((labels: { label: number; backgroundColor: string }[], dataset) => {
            dataset.backgroundColor.forEach((backgroundColor: string, i: number) => {
              const foundLabelIndex = labels.findIndex(label => label.backgroundColor === backgroundColor);

              if (foundLabelIndex === -1) {
                labels.push({ backgroundColor, label: dataset.data[i] });
              } else {
                labels[foundLabelIndex].label += dataset.data[i];
              }
            });

            return labels;
          }, [])
          .map(({ label, backgroundColor }) => (
            <Box key={backgroundColor} sx={{ display: "flex", alignItems: "center", m: 1 }}>
              <Box sx={{ backgroundColor, border: "1px solid #888888", mr: 2, width: 30, height: 30 }} />
              <Typography fontSize={20}>{label}</Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
};
